import { useState, useRef, useEffect } from "react";
import Title from "../../components/common/title/Title";
import Button from "../../components/common/button/Button";
import { postApi, putApi } from "../../services/api/globalApi";
import { useLocation, useNavigate } from "react-router";
import {
  successMessage,
  errorMessage,
  warningMessage,
} from "../../utils/toastify-message/ToastifyMessage";
import { PublicFileUrl, fileUrl } from "../../services/config";

const AVATAR_COLORS = [
  "#2E9C57", "#3F7CA8", "#C98A2E", "#8A6BB0",
  "#C0444A", "#1E9E4A", "#5B8C5A", "#A0522D",
];

const inputClass =
  "bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white";

const labelClass = "block mb-2 text-sm font-medium text-gray-900 dark:text-white";

function formatDurationFarsi(totalSec) {
  const mins = String(Math.floor(totalSec / 60)).padStart(2, "0");
  const secs = String(totalSec % 60).padStart(2, "0");
  return `${mins}:${secs}`.replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d]);
}

async function extractAudioDuration(file) {
  return new Promise((resolve) => {
    const AudioCtx = window.AudioContext || window["webkitAudioContext"];
    if (!AudioCtx) return resolve("");
    const ctx = new AudioCtx();
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const buffer = await ctx.decodeAudioData(e.target.result);
        resolve(formatDurationFarsi(Math.round(buffer.duration)));
      } catch {
        resolve("");
      } finally {
        ctx.close();
      }
    };
    reader.onerror = () => resolve("");
    reader.readAsArrayBuffer(file);
  });
}

async function extractVideoDuration(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(formatDurationFarsi(Math.round(video.duration)));
    };
    video.onerror = () => { URL.revokeObjectURL(url); resolve(""); };
    video.src = url;
  });
}

export default function TestimonialForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const isEdit = !!location.state;
  const editItem = location.state;

  const [type, setType] = useState(editItem?.type || "text");
  const [name, setName] = useState(editItem?.name || "");
  const [city, setCity] = useState(editItem?.city || "");
  const [avatarBg] = useState(
    editItem?.avatarBg || AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)]
  );
  const [quote, setQuote] = useState(editItem?.quote || "");
  const [stars, setStars] = useState(editItem?.stars ?? true);
  const [isActive, setIsActive] = useState(editItem?.isActive ?? true);

  const [duration, setDuration] = useState(editItem?.duration || "");
  const [voiceSrc, setVoiceSrc] = useState(editItem?.voiceSrc || "");
  const [voiceFile, setVoiceFile] = useState(null);
  const [voiceUploading, setVoiceUploading] = useState(false);
  const audioRef = useRef();

  const [shotSrc, setShotSrc] = useState(editItem?.shotSrc || "");
  const [shotFile, setShotFile] = useState(null);
  const [shotUploading, setShotUploading] = useState(false);
  const shotRef = useRef();

  const [videoSrc, setVideoSrc] = useState(editItem?.videoSrc || "");
  const [videoFile, setVideoFile] = useState(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const videoRef = useRef();

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (type !== "voice") { setVoiceSrc(""); setVoiceFile(null); }
    if (type !== "voice" && type !== "video") setDuration("");
    if (type !== "screenshot") { setShotSrc(""); setShotFile(null); }
    if (type !== "video") { setVideoSrc(""); setVideoFile(null); }
  }, [type]);

  const handleVoiceChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setVoiceFile(file);
    setVoiceSrc(URL.createObjectURL(file));
    const dur = await extractAudioDuration(file);
    setDuration(dur);
  };

  const handleShotChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setShotFile(file);
    setShotSrc(URL.createObjectURL(file));
  };

  const handleVideoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setVideoFile(file);
    setVideoSrc(URL.createObjectURL(file));
    const dur = await extractVideoDuration(file);
    setDuration(dur);
  };

  const uploadFile = async (file, setUploading) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await postApi({
        path: `${fileUrl}file`,
        body: formData,
      });
      return res?.data?.data || null;
    } catch {
      errorMessage("خطا در آپلود فایل");
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return warningMessage("نام را وارد کنید");
    if (!city.trim()) return warningMessage("شهر را وارد کنید");
    if (!quote.trim() && type !== "video") return warningMessage("متن نظر را وارد کنید");
    if (type === "voice" && !voiceSrc && !voiceFile)
      return warningMessage("فایل صوتی را انتخاب کنید");
    if (type === "screenshot" && !shotSrc && !shotFile)
      return warningMessage("تصویر اسکرین‌شات را انتخاب کنید");
    if (type === "video" && !videoSrc && !videoFile)
      return warningMessage("فایل ویدیو را انتخاب کنید");

    setLoading(true);
    try {
      let finalVoiceSrc = voiceSrc;
      let finalShotSrc = shotSrc;
      let finalVideoSrc = videoSrc;

      if (type === "voice" && voiceFile) {
        const filename = await uploadFile(voiceFile, setVoiceUploading);
        if (!filename) { setLoading(false); return; }
        finalVoiceSrc = filename;
      }

      if (type === "screenshot" && shotFile) {
        const filename = await uploadFile(shotFile, setShotUploading);
        if (!filename) { setLoading(false); return; }
        finalShotSrc = filename;
      }

      if (type === "video" && videoFile) {
        const filename = await uploadFile(videoFile, setVideoUploading);
        if (!filename) { setLoading(false); return; }
        finalVideoSrc = filename;
      }

      const body = {
        type,
        name,
        city,
        avatarBg,
        quote,
        stars,
        isActive,
        duration: (type === "voice" || type === "video") ? duration : "",
        voiceSrc: type === "voice" ? finalVoiceSrc : "",
        shotSrc: type === "screenshot" ? finalShotSrc : "",
        videoSrc: type === "video" ? finalVideoSrc : "",
      };

      if (isEdit) {
        await putApi({
          path: `admin/affiliate-testimonials/${editItem._id}`,
          body: JSON.stringify(body),
        });
        successMessage("نظر ویرایش شد");
      } else {
        await postApi({
          path: "admin/affiliate-testimonials",
          body: JSON.stringify(body),
        });
        successMessage("نظر اضافه شد");
      }
      navigate("/affiliate-testimonial-list");
    } catch {
      errorMessage("خطایی رخ داد");
    } finally {
      setLoading(false);
    }
  };

  const voicePreviewUrl = voiceFile
    ? URL.createObjectURL(voiceFile)
    : voiceSrc
    ? `${PublicFileUrl}public/files/${voiceSrc}`
    : null;

  const shotPreviewUrl = shotFile
    ? URL.createObjectURL(shotFile)
    : shotSrc && !shotSrc.startsWith("blob:")
    ? `${PublicFileUrl}public/files/${shotSrc}`
    : shotSrc || null;

  const videoPreviewUrl = videoFile
    ? URL.createObjectURL(videoFile)
    : videoSrc && !videoSrc.startsWith("blob:")
    ? `${PublicFileUrl}public/files/${videoSrc}`
    : videoSrc || null;

  return (
    <div className="bg-white dark:bg-gray-900 rounded-3xl p-6">
      <Title title={isEdit ? "ویرایش نظر نماینده" : "افزودن نظر نماینده"} />

      <form onSubmit={handleSubmit} className="mt-6 space-y-6">

        <div>
          <label className={labelClass}>نوع نظر</label>
          <div className="flex gap-3">
            {[
              { v: "text", label: "متنی" },
              { v: "voice", label: "صوتی" },
              { v: "screenshot", label: "تصویری" },
              { v: "video", label: "ویدیویی" },
            ].map(({ v, label }) => (
              <button
                key={v}
                type="button"
                onClick={() => setType(v)}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  type === v
                    ? "bg-green-600 border-green-600 text-white"
                    : "bg-gray-50 border-gray-300 text-gray-700 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 hover:border-green-500"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>نام نماینده</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="مثال: مریم رضایی"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>شهر / عنوان</label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="مثال: نماینده اصفهان"
              className={inputClass}
            />
          </div>
        </div>

        <div className={(type === "screenshot" || type === "video") ? "grid sm:grid-cols-2 gap-4 items-start" : ""}>
          <div>
            <label className={labelClass}>متن نظر</label>
            <textarea
              rows={3}
              value={quote}
              onChange={(e) => setQuote(e.target.value)}
              placeholder="متن نظر نماینده..."
              className={inputClass + " resize-y"}
            />
          </div>

          {type === "screenshot" && (
            <div>
              <label className={labelClass}>تصویر اسکرین‌شات</label>
              <input ref={shotRef} type="file" accept="image/*" onChange={handleShotChange} className="hidden" />
              {shotPreviewUrl ? (
                <div className="bg-gray-50 border border-gray-300 rounded-lg p-3 dark:bg-gray-700 dark:border-gray-600">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="w-14 h-14 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-500 shrink-0">
                      <img src={shotPreviewUrl} alt="پیش‌نمایش" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {shotFile?.name || shotSrc}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => shotRef.current?.click()} className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400">
                        تغییر
                      </button>
                      <button type="button" onClick={() => { setShotSrc(""); setShotFile(null); }} className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 dark:bg-red-900/30 dark:text-red-400">
                        حذف
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div onClick={() => shotRef.current?.click()} className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors dark:bg-gray-700 dark:border-gray-600 dark:hover:border-blue-500">
                  <div className="flex flex-col items-center">
                    <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm text-gray-500 dark:text-gray-400">کلیک کنید برای انتخاب تصویر</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {type === "video" && (
            <div>
              <label className={labelClass}>فایل ویدیو</label>
              <input ref={videoRef} type="file" accept="video/*" onChange={handleVideoChange} className="hidden" />
              {videoPreviewUrl ? (
                <div className="bg-gray-50 border border-gray-300 rounded-lg p-3 dark:bg-gray-700 dark:border-gray-600 space-y-2">
                  <video src={videoPreviewUrl} controls className="w-full rounded-lg max-h-40 object-contain bg-black" />
                  {duration && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">مدت زمان: <span dir="ltr">{duration}</span></p>
                  )}
                  <div className="flex gap-2">
                    <button type="button" onClick={() => videoRef.current?.click()} className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400">
                      تغییر
                    </button>
                    <button type="button" onClick={() => { setVideoSrc(""); setVideoFile(null); setDuration(""); }} className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 dark:bg-red-900/30 dark:text-red-400">
                      حذف
                    </button>
                  </div>
                </div>
              ) : (
                <div onClick={() => videoRef.current?.click()} className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-purple-400 hover:bg-purple-50/50 transition-colors dark:bg-gray-700 dark:border-gray-600 dark:hover:border-purple-500">
                  <div className="flex flex-col items-center">
                    <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.069A1 1 0 0121 8.914v6.172a1 1 0 01-1.447.894L15 13.5M4 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H6a2 2 0 01-2-2V8z" />
                    </svg>
                    <p className="text-sm text-gray-500 dark:text-gray-400">کلیک کنید برای انتخاب ویدیو</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">مدت زمان به‌صورت خودکار تشخیص داده می‌شود</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {type === "voice" && (
          <div>
            <input ref={audioRef} type="file" accept="audio/*" onChange={handleVoiceChange} className="hidden" />
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>فایل صوتی</label>
                <div className="flex items-center gap-3 flex-wrap">
                  <button type="button" onClick={() => audioRef.current?.click()} className="px-4 py-2 text-sm rounded-lg border border-gray-300 bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white hover:border-green-500 transition-colors">
                    {voiceFile || voiceSrc ? "تغییر فایل" : "انتخاب فایل صوتی"}
                  </button>
                  {voiceFile && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px]">{voiceFile.name}</span>
                  )}
                </div>
              </div>
              <div>
                <label className={labelClass}>مدت زمان (خودکار)</label>
                <input type="text" value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="۰۰:۴۸" className={inputClass} dir="ltr" />
              </div>
            </div>
            {voicePreviewUrl && (
              <audio src={voicePreviewUrl} controls className="mt-3 w-full max-w-sm" />
            )}
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>نمایش ستاره‌ها</label>
            <select
              value={stars ? "true" : "false"}
              onChange={(e) => setStars(e.target.value === "true")}
              className={inputClass}
            >
              <option value="true">نمایش داده شود</option>
              <option value="false">نمایش داده نشود</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>وضعیت</label>
            <select
              value={isActive ? "true" : "false"}
              onChange={(e) => setIsActive(e.target.value === "true")}
              className={inputClass}
            >
              <option value="true">فعال</option>
              <option value="false">غیرفعال</option>
            </select>
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            text={
              loading || voiceUploading || shotUploading || videoUploading
                ? "در حال ذخیره..."
                : isEdit
                ? "ویرایش"
                : "افزودن"
            }
            type="submit"
            color="success"
            disabled={loading || voiceUploading || shotUploading || videoUploading}
          />
          <Button
            text="انصراف"
            type="button"
            color="light"
            onClick={() => navigate("/affiliate-testimonial-list")}
          />
        </div>
      </form>
    </div>
  );
}